import { Wrench, MapPin, Shield, Clock, PhoneCall, ClipboardCheck, Settings } from 'lucide-react';
import type { CommuneData } from '@/data/communes';

export function TrustCommune({ commune }: { commune: CommuneData }) {
  const { nom } = commune;

  return (
    <>
      {/* Pourquoi nous */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="fade-up max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="section-title text-center mb-4">Pourquoi nous choisir à {nom} ?</h2>
          <p className="section-subtitle text-center mb-10">
            Des réparations de qualité, un service local et une garantie qui vous protège
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Wrench, title: 'Réparation avant remplacement', desc: `À ${nom}, nous analysons la panne et réparons chaque fois que c'est possible. Une approche économique et écologique.` },
              { icon: MapPin, title: 'Intervention locale rapide', desc: `Basés à Charleroi, nous arrivons rapidement à ${nom} et dans toutes les communes du Hainaut.` },
              { icon: Shield, title: 'Garantie 10 ans', desc: 'Toutes nos réparations et installations sont garanties 10 ans, pièces et main-d\'œuvre incluses.' },
              { icon: Clock, title: 'Disponible 7j/7', desc: `Un volet bloqué est une urgence. Nous intervenons rapidement à ${nom}, y compris le week-end pour les dépannages urgents.` },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="card border border-background-200">
                  <div className="w-11 h-11 rounded-xl bg-accent-50 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-accent-500" />
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-700 text-sm mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="fade-up max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="section-title text-center mb-10">Comment ça marche ?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
            {[
              {
                step: '1',
                icon: PhoneCall,
                title: 'Appelez ou demandez un devis',
                desc: `Contactez-nous par téléphone ou via notre formulaire. Décrivez votre panne, nous vous conseillons immédiatement et fixons un rendez-vous à ${nom}.`,
              },
              {
                step: '2',
                icon: ClipboardCheck,
                title: 'Diagnostic sur place',
                desc: `Notre technicien se déplace à ${nom}, diagnostique la panne et vous propose un devis clair avant toute intervention.`,
              },
              {
                step: '3',
                icon: Settings,
                title: 'Réparation garantie',
                desc: 'Nous réparons votre volet avec des pièces de qualité. Travail soigné, propre, et garanti 10 ans.',
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="relative text-center">
                  <div className="w-14 h-14 rounded-full bg-accent-500 flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-heading font-bold text-xl">{item.step}</span>
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-accent-50 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-5 h-5 text-accent-500" />
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-700 text-sm mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}